<script setup lang="ts">
import { mechanicVisuals } from '../mechanicVisuals'

import type { ContestMechanic } from '@/types/global'
import type { ContestTemplateReward, TemplateData, TemplateDetailContestsResponse } from '@/types/types'

const props = defineProps<{
  mechanic?: ContestMechanic
  template?: TemplateData
}>()

const { t } = useI18n()
const { formatNumber } = useNumber()
const { fetchTemplateDetail } = useTemplate()

const detail = ref<TemplateDetailContestsResponse>()
const hasFailed = ref(false)

const mechanic = computed(() => props.mechanic ?? detail.value?.mechanic)

const visual = computed(() => (mechanic.value ? mechanicVisuals[mechanic.value] : undefined))

const fetchDetail = async (templateId: number) => {
  detail.value = undefined
  hasFailed.value = false

  try {
    const { templatable } = await fetchTemplateDetail<TemplateDetailContestsResponse>(templateId)
    if (props.template?.id === templateId) detail.value = templatable
  } catch {
    if (props.template?.id === templateId) hasFailed.value = true
  }
}

watch(
  () => props.template?.id,
  (templateId) => {
    if (templateId) fetchDetail(templateId)
    else {
      detail.value = undefined
      hasFailed.value = false
    }
  },
  { immediate: true }
)

const mechanicLabel = computed(() => (mechanic.value ? t(`pages.contests.mechanics.${mechanic.value}.title`) : null))

const goal = computed(() => {
  const slug = detail.value?.contestGameBoostType?.slug
  return slug ? t(`pages.contests.contestsCreatePage.global.form.fields.boost.${slug}.title`) : null
})

const participation = computed(() => {
  const days = props.template ? (detail.value?.daysBetweenParticipations ?? 0) : 0
  return days > 0
    ? t('pages.contests.modal.detail.daysBetweenParticipations.everyday', { day: days })
    : t('pages.contests.modal.detail.daysBetweenParticipations.once')
})

const rewards = computed(() => (props.template ? (detail.value?.contestGameRewards ?? []) : []))

const losingProbability = computed(() => {
  if (!props.template || !detail.value?.canLose) return undefined

  return detail.value.losingProbability ?? 0
})

const rewardLabel = (reward: ContestTemplateReward) => {
  if (reward.name) return reward.name

  const slug = reward.rewardTypeSlug ?? reward.rewardType?.slug
  return slug ? t(`pages.contests.modal.detail.rewardType.types.${slug}`, { count: 1 }, 1) : ''
}

const isReady = computed(() => !props.template || !!detail.value)
</script>

<template>
  <div v-if="isReady" class="flex h-full flex-col gap-4">
    <div class="flex items-center gap-2.5">
      <div
        v-if="visual"
        class="flex size-9 shrink-0 items-center justify-center rounded-[10px]"
        :class="visual.chipClass"
      >
        <component :is="visual.icon" class="size-[18px]" :class="visual.iconClass" :stroke-width="1.75" />
      </div>
      <div class="flex min-w-0 flex-col">
        <p class="line-clamp-2 text-sm font-semibold leading-[18px] text-foreground">
          {{ template ? template.name : t('pages.contests.modal.skipObjective') }}
        </p>
        <p v-if="mechanicLabel" class="truncate text-xs leading-4 text-foreground-secondary">{{ mechanicLabel }}</p>
      </div>
    </div>

    <div class="flex flex-col gap-2.5 text-xs">
      <div v-if="goal" class="flex gap-2">
        <p class="w-[88px] shrink-0 text-foreground-secondary">
          {{ t('pages.contests.modal.detail.labels.goal') }}
        </p>
        <p class="font-medium text-foreground">{{ goal }}</p>
      </div>
      <div class="flex gap-2">
        <p class="w-[88px] shrink-0 text-foreground-secondary">
          {{ t('pages.contests.modal.detail.labels.participation') }}
        </p>
        <p class="font-medium text-foreground">{{ participation }}</p>
      </div>
    </div>

    <p v-if="!template" class="border-t border-border pt-3.5 text-[13px] text-foreground-secondary">
      {{ t('pages.contests.modal.detail.defineInEditor') }}
    </p>

    <div v-else class="flex flex-col gap-2 border-t border-border pt-3.5">
      <p class="text-xs text-foreground-secondary">{{ t('pages.contests.modal.detail.labels.rewards') }}</p>

      <div v-for="(reward, index) in rewards" :key="index" class="flex items-center gap-2">
        <span class="size-1.5 shrink-0 rounded-full bg-current" :class="visual?.iconClass" />
        <p class="flex-1 truncate text-[13px] font-medium leading-[18px] text-foreground">{{ rewardLabel(reward) }}</p>
        <p v-if="reward.probability" class="shrink-0 whitespace-nowrap text-right text-xs text-foreground-secondary">
          {{ formatNumber(reward.probability) }} %
        </p>
      </div>

      <div v-if="losingProbability !== undefined" class="flex items-center gap-2">
        <span class="size-1.5 shrink-0 rounded-full bg-border" />
        <p class="flex-1 truncate text-[13px] leading-[18px] text-foreground-secondary">
          {{ t('pages.contests.modal.detail.labels.losing') }}
        </p>
        <p v-if="losingProbability" class="shrink-0 whitespace-nowrap text-right text-xs text-foreground-secondary">
          {{ formatNumber(losingProbability) }} %
        </p>
      </div>

      <p v-if="!rewards.length && losingProbability === undefined" class="text-[13px] text-foreground-secondary">
        {{ t('pages.contests.modal.detail.rewardsToDefine') }}
      </p>
    </div>
  </div>

  <p v-else-if="hasFailed" class="text-[13px] text-foreground-secondary">
    {{ t('pages.contests.modal.detail.loadError') }}
  </p>

  <AppSkeleton v-else type="text" class="h-40 w-full" />
</template>
